import { RPURequestStatusType } from '../../models/types/RPURequestStatusType';
import { Batu } from '../interface/Batu';
export class BatuWS extends EventTarget implements Batu {
  //#region Properties
  private readonly URL: string;
  public readonly rpu: string;
  public readonly email: string;
  public readonly id: string;
  public readonly datetime: Date;
  public readonly recoveredBills: number = 0;
  public readonly timeElapsed: number = 0;
  public csvLink: string = '';
  public pdfLink: string = '';
  public errorMessage: string = '';
  public progress: number = 0;
  public serviceName: string = '';
  public latestTotal: number = 0;
  public status: RPURequestStatusType = RPURequestStatusType.IN_PROCESS;
  private boolConnected: boolean = false;
  private startTime: number = 0;
  private endTime: number = 0;
  private retryCounter: number = 0;
  private ws: WebSocket = {} as WebSocket;
  private requestToken: string = '';
  private timer1: NodeJS.Timeout = {} as NodeJS.Timeout;
  private timer2: NodeJS.Timeout = {} as NodeJS.Timeout;
  private timer3: NodeJS.Timeout = {} as NodeJS.Timeout;

  private FAILURE_RETRIES = 3;
  private CONTRACT_DATA_PROGRESS = 10;
  private CONTRACT_DATA_TIMEOUT = 2 * 60 * 1000; //minutes * seconds * milliseconds
  private GOV_JOB_PROGRESS = 32;
  private GOV_JOB_TIMEOUT = 2 * 60 * 1000; //minutes * seconds * milliseconds
  private COMPLETE_PROGRESS = 100;
  private COMPLETE_TIMEOUT = 6 * 60 * 1000; //minutes * seconds * milliseconds
  private CFE_UNAVAILABLE_MESSAGE =
    'El servicio de CFE no esta disponible, intenta nuevamente en un momento';
  private HEADLESS_CHROME_MESSAGE = 'headless chrome';
  private PARAMETERS_MESSAGE = 'unable to retrieve parameters';

  private errorMessages = {
    200: 'rpu or service not available',
    300: 'Unable to download files',
    400: 'CFE web page is unavailable',
    401: 'CFE contigo page is unavailable',
    402: 'CFE contigo add service unavailable',
    404: 'contract data site not available',
    501: 'Cache failure',
    502: 'Get contract data failed',
    503: 'Failed to get latest total',
    504: 'Failed to get gov bills',
    505: 'Failed to get mi espacio bills',
    506: 'Failed to process bills',
    507: 'Failed to generate results',
  };

  private translations = {
    'unable to access rpu history.':
      'El RPU no tiene historial registrado, revisa que sea correcto, pudo haber sido dado de baja o que sea un servicio nuevo.',
    'Latest total is incorrect':
      'Necesitamos el monto del último recibo para registrarlo, intenta nuevamente para agregarlo.',
    'Unable to register bill':
      'No es posible registrar el RPU, revisa que sea correcto.',
    'invalid rpu':
      'No encontramos el RPU, revisa que sea correcto, pudo haber sido dado de baja o que sea un servicio nuevo.',
    'rpu not available':
      'No encontramos el RPU, revisa que sea correcto, pudo haber sido dado de baja o que sea un servicio nuevo.',
    'rpu does not exist':
      'No encontramos el RPU, revisa que sea correcto, pudo haber sido dado de baja o que sea un servicio nuevo.',
    'rpu or service not available':
      'RPU o servicio no disponible, intenta nuevamente en unos minutos.',
    'Unable to download files':
      'No se pudieron descargar los archivos, intenta nuevamente en unos minutos.',
    'CFE web page is unavailable':
      'La página web de CFE no está disponible, intenta nuevamente en unos minutos.',
    'CFE contigo page is unavailable':
      'La página de CFE contigo no está disponible, intenta nuevamente en unos minutos.',
    'CFE contigo add service unavailable':
      'No se puede agregar el servicio de CFE contigo, intenta nuevamente en unos minutos.',
    'contract data site not available':
      'El sitio de datos del contrato no está disponible, intenta nuevamente en unos minutos.',
    'Cache failure': 'Fallo en la caché, intenta nuevamente en unos minutos.',
    'Get contract data failed':
      'Error al obtener los datos del contrato, intenta nuevamente en unos minutos.',
    'Failed to get latest total':
      'Error al obtener el último total, intenta nuevamente en unos minutos.',
    'Failed to get gov bills':
      'Error al obtener las facturas gubernamentales, intenta nuevamente en unos minutos.',
    'Failed to get mi espacio bills':
      'Error al obtener las facturas de Mi Espacio, intenta nuevamente en unos minutos.',
    'Failed to process bills':
      'Error al procesar las facturas, intenta nuevamente en unos minutos.',
    'Failed to generate results':
      'Error al generar los resultados, intenta nuevamente en unos minutos.',
  };
  constructor(url: string, rpu: string, id: string, email: string) {
    super();
    this.URL = url;
    this.rpu = rpu;
    this.email = email;
    this.id = id;
    this.datetime = new Date();
  }
  requestRpu(
    token?: string,
    serviceName?: string,
    latestTotal?: number,
    resetCounter?: boolean
  ): void {
    this.resetState();
    if (token) {
      this.requestToken = token;
    }
    if (resetCounter) {
      this.retryCounter = 0;
    }
    if (serviceName !== undefined) {
      this.serviceName = serviceName;
      this.updateServiceName(serviceName);
    }
    if (latestTotal) {
      this.latestTotal = latestTotal;
    }
    this.ws = new WebSocket(`${this.URL}?idToken=${this.requestToken}`);
    this.setWS();
  }
  private setWS() {
    this.ws.onclose = () => {
      this.boolConnected = false;
      this.onDisconnected();
    };
    this.ws.onmessage = (event) => {
      this.handleWSMessage(event.data);
    };
    this.ws.onerror = (event) => {
      console.error(event);
    };
    this.ws.onopen = () => {
      this.boolConnected = true;
      this.onConnected();
      this.startTime = performance.now();
      if (this.serviceName === '') {
        this.ws.send(
          JSON.stringify({
            action: 'start_job',
            message: {
              rpu: this.rpu,
              email: this.email,
            },
          })
        );
        this.setProgressWatchers('gov');
      } else {
        this.ws.send(
          JSON.stringify({
            action: 'start_job',
            message: {
              route: 'mi_espacio',
              rpu: this.rpu,
              email: this.email,
              service_name: this.serviceName,
              latest_total: this.latestTotal,
            },
          })
        );
        this.setProgressWatchers('mi_espacio');
      }
    };
  }
  private triggerTimeout() {
    if (this.retryCounter < this.FAILURE_RETRIES) {
      console.log('Retrying due to timeout: ', this.retryCounter);
      this.retryCounter++;
      this.ws.close();
      this.requestRpu();
    } else {
      let error = this.CFE_UNAVAILABLE_MESSAGE;
      this.status = RPURequestStatusType.ERROR;
      this.errorMessage = error;
      this.dispatchStatusEvent(RPURequestStatusType.ERROR);
      this.dispatchErrorEvent(error);
      this.ws.close();
    }
  }
  private setProgressWatchers(jobType: string) {
    if (jobType === 'mi_espacio') {
      this.timer1 = setTimeout(() => {
        if (
          this.progress < this.COMPLETE_PROGRESS &&
          this.status === RPURequestStatusType.IN_PROCESS
        ) {
          console.warn('Timeout Mi espacio');
          this.triggerTimeout();
        }
      }, this.COMPLETE_TIMEOUT);
    } else {
      this.timer1 = setTimeout(() => {
        if (
          this.progress < this.CONTRACT_DATA_PROGRESS &&
          this.status === RPURequestStatusType.IN_PROCESS
        ) {
          console.warn('Timeout Contract');
          this.triggerTimeout();
        } else {
          this.timer3 = setTimeout(() => {
            if (
              this.progress < this.GOV_JOB_PROGRESS &&
              this.status === RPURequestStatusType.IN_PROCESS
            ) {
              console.warn('Timeout Gov');
              this.ws.close();
              this.requestRpu();
            }
          }, this.GOV_JOB_TIMEOUT);
        }
      }, this.CONTRACT_DATA_TIMEOUT);
      this.timer2 = setTimeout(() => {
        if (
          this.progress < this.COMPLETE_PROGRESS &&
          this.status === RPURequestStatusType.IN_PROCESS
        ) {
          console.warn('Timeout Full');
          this.triggerTimeout();
        }
      }, this.COMPLETE_TIMEOUT);
    }
  }
  private resetState() {
    this.status = RPURequestStatusType.IN_PROCESS;
    this.progress = 0;
    this.errorMessage = '';
    this.csvLink = '';
    this.pdfLink = '';
    clearTimeout(this.timer1);
    clearTimeout(this.timer2);
    clearTimeout(this.timer3);
  }
  public isConnected(): boolean {
    return this.boolConnected;
  }
  public addEventListener(
    type: string,
    callback: EventListenerOrEventListenerObject | null,
    options?: boolean | AddEventListenerOptions | undefined
  ): void {
    super.addEventListener(type, callback, options);
  }
  public removeEventListener(
    type: string,
    callback: EventListenerOrEventListenerObject | null,
    options?: boolean | EventListenerOptions | undefined
  ): void {
    super.removeEventListener(type, callback, options);
  }
  public dispatchEvent(event: Event): boolean {
    return super.dispatchEvent(event);
  }
  private onConnected() {
    const connectedEvent = new Event('connection');
    this.dispatchEvent(connectedEvent);
  }
  private onDisconnected() {
    const disconnectedEvent = new Event('disconnection');
    this.dispatchEvent(disconnectedEvent);
  }
  private dispatchProgressEvent(progress: number) {
    const progressEvent = new CustomEvent('progress', { detail: progress });
    this.dispatchEvent(progressEvent);
  }
  private dispatchStatusEvent(status: RPURequestStatusType) {
    const statusEvent = new CustomEvent('status', { detail: status });
    this.dispatchEvent(statusEvent);
  }
  private dispatchCFECredentialsEvent() {
    const cfecredentialsEvent = new Event('cfecredentials');
    this.dispatchEvent(cfecredentialsEvent);
  }
  private dispatchResultsEvent(
    json: any,
    csvLink: string,
    pdfLink: string,
    recoveredBills: number,
    timeElapsed: number
  ) {
    const resultsEvent = new CustomEvent('results', {
      detail: { json, csvLink, pdfLink, timeElapsed, recoveredBills },
    });
    this.dispatchEvent(resultsEvent);
  }
  private dispatchServiceNameEvent(serviceName: string) {
    const serviceNameEvent = new CustomEvent('serviceName', {
      detail: serviceName,
    });
    this.dispatchEvent(serviceNameEvent);
  }
  private dispatchErrorEvent(errorMessage: string) {
    const errorEvent = new CustomEvent('error', {
      detail: {
        errorMessage: errorMessage,
        rpu: this.rpu,
        email: this.email,
        datetime: this.datetime,
      },
    });
    this.dispatchEvent(errorEvent);
  }
  private updateServiceName(serviceName: string) {
    if (serviceName) {
      this.serviceName = serviceName;
      this.dispatchServiceNameEvent(serviceName);
    }
  }
  private handleProgress(json: any) {
    switch (json.data?.message) {
      case 'cache validated':
        if (json.data.data_in_cache) {
          this.dispatchProgressEvent(95);
          this.progress = 95;
          break;
        }
        this.dispatchProgressEvent(5);
        this.progress = 5;
        break;
      case 'contract data':
        this.dispatchProgressEvent(10);
        this.progress = 10;
        this.updateServiceName(json.data?.body?.service_name);
        break;
      case 'start job Gov':
        this.dispatchProgressEvent(11);
        this.progress = 11;
        break;
      case 'login Gov successful':
        this.dispatchProgressEvent(20);
        this.progress = 20;
        break;
      case 'first bill processed':
        this.dispatchProgressEvent(25);
        this.progress = 25;
        break;
      case 'requires Mi Espacio job':
        this.progress = this.progress + 2;
        this.dispatchProgressEvent(this.progress + 2);
        break;
      case 'bill processed':
        if (this.progress < 90) {
          this.progress = this.progress + 3;
          this.dispatchProgressEvent(this.progress + 3);
        }
        break;
      case 'start job Mi Espacio':
        this.progress = this.progress + 2;
        this.dispatchProgressEvent(this.progress + 2);
        break;
      case 'login Mi Espacio successful':
        this.dispatchProgressEvent(50);
        this.progress = 50;
        break;
      case 'bill not registered':
        this.progress = this.progress + 2;
        this.dispatchProgressEvent(this.progress + 2);
        break;
      case 'job Gov finished':
        this.progress = this.progress + 2;
        this.dispatchProgressEvent(this.progress + 2);
        break;
      case 'bill register successful':
        this.progress = this.progress + 5;
        this.dispatchProgressEvent(this.progress + 5);
        break;
      case 'bill already registered':
        this.progress = this.progress + 2;
        this.dispatchProgressEvent(this.progress + 2);
        break;
      case 'job Mi Espacio finished':
        this.dispatchProgressEvent(95);
        this.progress = 95;
        break;
      case 'CFE Contigo credentials have not been registered':
        this.dispatchCFECredentialsEvent();
        break;
      default:
        break;
    }
  }
  private handleErrors(json: any) {
    console.log('Error: ', json);
    if (
      json.data?.message.includes(this.HEADLESS_CHROME_MESSAGE) ||
      json.data?.message.includes(this.PARAMETERS_MESSAGE) ||
      (this.errorMessages.hasOwnProperty(json.data?.code) &&
        this.retryCounter < this.FAILURE_RETRIES)
    ) {
      console.log('Retrying due to error: ', this.retryCounter, json);
      this.retryCounter++;
      this.ws.close();
      this.requestRpu();
      return;
    }
    console.log(this.translations.hasOwnProperty(json.data?.message));
    console.log(
      this.translations[json.data?.message as keyof typeof this.translations]
    );

    if (this.translations.hasOwnProperty(json.data?.message)) {
      this.errorMessage =
        this.translations[json.data?.message as keyof typeof this.translations];
    } else {
      this.errorMessage = json.data?.message;
    }
    this.dispatchStatusEvent(RPURequestStatusType.ERROR);
    this.status = RPURequestStatusType.ERROR;
    this.dispatchErrorEvent(this.errorMessage);
  }
  private handleWSMessage(event: any) {
    let json = JSON.parse(event);
    if (json.status && (json.status === 'fail' || json.status === 'error')) {
      this.status = RPURequestStatusType.ERROR;
      if (json.message !== '') {
        this.errorMessage = json.message;
      } else if (json.data?.message !== '') {
        this.errorMessage = json.data?.message;
      }
      this.dispatchStatusEvent(RPURequestStatusType.ERROR);
      this.dispatchErrorEvent(this.errorMessage);
      return;
    }
    if (json.type) {
      switch (json.type) {
        case 'start':
          this.dispatchProgressEvent(1);
          this.progress = 1;
          break;
        case 'progress':
          this.handleProgress(json);
          break;
        case 'error':
          this.handleErrors(json);
          break;
        case 'end':
          this.endTime = performance.now();
          let timeElapsed = (this.endTime - this.startTime) / 1000;
          this.dispatchProgressEvent(100);
          this.progress = 100;
          if (json.data?.csv && json.data?.csv.length > 0) {
            this.dispatchStatusEvent(RPURequestStatusType.SUCCESS);
            var count = json.data.json.reduce(function (n: any, val: any) {
              return n + (val.billTotal !== null);
            }, 0);
            this.dispatchResultsEvent(
              json.data.json,
              json.data.csv,
              json.data.files,
              count,
              timeElapsed
            );
            this.status = RPURequestStatusType.SUCCESS;
            this.csvLink = json.data.csv;
            this.pdfLink = json.data.files;
          } else {
            this.dispatchStatusEvent(RPURequestStatusType.ERROR);
            this.status = RPURequestStatusType.ERROR;
            this.errorMessage = json.data?.message;
            this.dispatchErrorEvent(json.data?.message);
          }
          break;

        default:
          break;
      }
    }
  }
}
